import React, { FC, useMemo } from 'react';
import classnames from 'classnames';
import { Formik } from 'formik';
import { Checkbox } from '@clearcover/atlas';
import { FormInputErrorMessage } from 'Shared/components';
import { Label } from 'Shared/components/FormInput/Variants';
import { combineStrings } from 'Shared/utils';
import { GetConfigResponseType } from 'QuoteApp/services/endpoints/GetConfig';
import { DriverDataType } from 'QuoteApp/services/endpoints/GetDetails';
import { transformSaveVehicleDriverAssignmentRequest } from 'QuoteApp/services/endpoints/PostSaveVehicleDriverAssignment';
import { useAppContext } from 'QuoteApp/services/providers';
import { FormErrorBanner } from '../FormErrorBanner';
import styles from './styles.module.css';
import { mergeInProgressDataFromSummary } from 'QuoteApp/screens/Summary/Summary';

const formatDriverDisplay = (
  driver: DriverDataType,
  config: GetConfigResponseType,
  index: number
) =>
  driver.firstName.value === ''
    ? `Driver ${index + 1}`
    : combineStrings([
        driver.firstName.value,
        driver.middleName.value,
        driver.lastName.value,
        config.drivers.personalInfo.suffix[driver.suffix.value],
      ]);

export const getDriversWithoutAssignments = (
  drivers: Record<string, DriverDataType>,
  vehicleDriverAssignment: Record<string, string[]> | null
): string[] => {
  if (!vehicleDriverAssignment) return [];
  return Object.keys(drivers).filter(driverId => {
    const isDriverAssigned = Object.keys(
      vehicleDriverAssignment
    ).some(vehicleId => vehicleDriverAssignment[vehicleId].includes(driverId));
    return (
      drivers[driverId].driverType !== 'excludedPerson' && !isDriverAssigned
    );
  });
};

type VehicleDriverAssignmentPropsType = {
  editable?: boolean;
  showErrors?: boolean;
};

export const VehicleDriverAssignment: FC<VehicleDriverAssignmentPropsType> = ({
  showErrors = true,
  editable = true,
}) => {
  const { details, config, setDetails, refreshRate, api } = useAppContext();

  const initialValues = useMemo(
    () =>
      Object.keys(details.vehicles).reduce<Record<string, Array<string>>>(
        (acc, vehicleId) => ({
          ...acc,
          [vehicleId]: details.vehicleDriverAssignment![vehicleId] || [],
        }),
        {}
      ),
    [details.vehicleDriverAssignment, details.vehicles]
  );

  const driversWithoutAssignments = useMemo(
    () => getDriversWithoutAssignments(details.drivers, initialValues),
    [details.drivers, initialValues]
  );

  if (!config) return null;

  return (
    <div
      className={styles.wrapper}
      role="group"
      aria-labelledby="vehicle-assignment"
    >
      <div className={styles.header}>
        <h3>Please let us know which drivers will drive each vehicle.</h3>
        <p>
          Each driver must be assigned to at least one vehicle. Select all that
          apply.
        </p>
        {driversWithoutAssignments.length > 0 && showErrors && (
          <FormErrorBanner
            className="fs-mask"
            /* map through all drivers instead of just driverWithoutAssignments to keep
               same index position (i) */
            message={`${Object.entries(details.drivers)
              .map(([driverId, driver], i) =>
                driversWithoutAssignments.includes(driverId)
                  ? formatDriverDisplay(driver, config, i)
                  : null
              )
              .filter(d => d !== null)
              .sort()
              .join(', ')} must be assigned to at least one vehicle.`}
          />
        )}
      </div>

      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={async (data, actions) => {
          try {
            const response = await api.saveAppData({
              data,
              transformRequest: transformSaveVehicleDriverAssignmentRequest,
            });
            const mergedDetails = mergeInProgressDataFromSummary(
              details,
              response.data
            );
            setDetails(mergedDetails);
            refreshRate();
          } catch {
            actions.resetForm();
          } finally {
            actions.setSubmitting(false);
          }
        }}
      >
        {({ values, isSubmitting, handleSubmit, setFieldValue }) => {
          return Object.entries(values).map(
            ([vehicleId, driverAssignments], i) => {
              return (
                <div
                  key={vehicleId}
                  className={classnames('fs-mask', styles.vehicle)}
                >
                  <Label
                    id="vehicle-assignment"
                    className={styles.vehicleLabel}
                  >
                    {details.vehicles[vehicleId]?.year.value === ''
                      ? `Vehicle ${i + 1}`
                      : combineStrings([
                          details.vehicles[vehicleId]?.year.value,
                          details.vehicles[vehicleId]?.make.value,
                          details.vehicles[vehicleId]?.model.value,
                        ])}
                  </Label>
                  <div className={styles.checkboxContainer}>
                    {Object.entries(details.drivers)
                      .filter(
                        ([_, driver]) => driver.driverType !== 'excludedPerson'
                      )
                      .map(([driverId, driver], i) => {
                        return (
                          <Checkbox
                            data-testid="vehicle-assignment-checkbox"
                            className={styles.checkbox}
                            disabled={isSubmitting || !editable}
                            key={driverId}
                            name={`${vehicleId}-${driverId}`}
                            label={formatDriverDisplay(driver, config, i)}
                            value={driverAssignments.includes(driverId)}
                            onChange={checked => {
                              const drivers = checked
                                ? [...values[vehicleId], driverId]
                                : values[vehicleId].filter(d => d !== driverId);
                              setFieldValue(vehicleId, drivers);
                              handleSubmit();
                            }}
                          />
                        );
                      })}
                  </div>
                  {values[vehicleId].length === 0 && showErrors && (
                    <FormInputErrorMessage message="Each vehicle must have at least one driver." />
                  )}
                </div>
              );
            }
          );
        }}
      </Formik>
    </div>
  );
};
